import interviewService from "../../../../services/interviewService";

// ─── MODULE CARDS (for selection screen) ──────────────────────────────────────
export const GST_PERCENT = 18;

export const MODULE_CARDS = [
  {
    id: "easy",
    name: "Easy",
    label: "Foundation",
    color: "#068434",
    sets: ["set1", "set2"],
    questionsPerSet: 10,
    price: 0,
    priceWithGST: 0,
    gstPercent: 0,
    currency: "INR",
    locked: false,
    badge: "Free",
    maxAttempts: 2,
    description: "Core visa interview questions covering purpose, duration, and basic financials.",
    topics: ["Purpose of Travel", "Duration of Stay", "Basic Financials", "Employment Status"],
    features: [
      "2 available sets",
      "10 questions per set",
      "2 total attempts (use freely)",
      "AI-evaluated answers",
    ],
    upgradeTarget: "medium",
  },
  {
    id: "medium",
    name: "Medium",
    label: "Intermediate",
    color: "#0b73b8",
    colorBg: "rgba(245,158,11,0.08)",
    colorBorder: "rgb(245, 159, 11)",
    sets: ["set1", "set2", "set3"],
    questionsPerSet: 10,
    price: 299,
    priceWithGST: 353,
    gstPercent: 18,
    currency: "INR",
    locked: false,
    badge: "₹299 + GST",
    maxAttempts: 3,
    description: "Advanced questioning on ties to home country, prior travel, and US contacts.",
    topics: ["Home Country Ties", "Prior Travel History", "US Contacts", "Immigration Intent"],
    features: [
      "3 available sets",
      "10 questions per set",
      "3 total attempts (use freely)",
      "AI-evaluated answers with ideal responses",
      "Detailed performance scorecard",
    ],
    upgradeTarget: "pro",
  },
  {
    id: "pro",
    name: "Pro",
    label: "Advanced",
    color: "#891515",
    colorBg: "rgba(239,68,68,0.08)",
    colorBorder: "rgba(239,68,68,0.2)",
    sets: ["set1", "set2", "set3", "set4", "set5"],
    questionsPerSet: 10,
    price: 599,
    priceWithGST: 707,
    gstPercent: 18,
    currency: "INR",
    locked: false,
    badge: "₹599 + GST",
    maxAttempts: 5,
    description: "Comprehensive scenario-based questioning with complex follow-up questions.",
    topics: ["Complex Scenarios", "Inconsistency Probing", "Documentation Deep Dive", "Officer Challenges"],
    features: [
      "5 available sets",
      "10 questions per set",
      "5 total attempts (use freely)",
      "AI-evaluated answers with ideal responses",
      "Advanced scenario-based questioning",
    ],
    upgradeTarget: null,
  },
];

// ─── MODULE CONFIGURATION (legacy, kept for compatibility) ───────────────────
export const MODULE_CONFIG = {
  easy: {
    id: "easy",
    label: "Foundation",
    level: "Easy",
    sets: 2,
    attemptsAllowed: 2,
    freeAttempts: 2,
    price: 0,
    gst: 0,
    total: 0,
    description: "Core visa interview questions covering purpose, duration, and basic financials.",
    topics: ["Purpose of Travel", "Duration of Stay", "Basic Financials", "Employment Status"],
  },
  medium: {
    id: "medium",
    label: "Intermediate",
    level: "Medium",
    sets: 2,
    attemptsAllowed: 3,
    freeAttempts: 0,
    price: 299,
    gst: 53.82,
    total: 352.82,
    description: "Advanced questioning on ties to home country, prior travel, and US contacts.",
    topics: ["Home Country Ties", "Prior Travel History", "US Contacts", "Immigration Intent"],
  },
  difficult: {
    id: "difficult",
    label: "Advanced",
    level: "Difficult",
    sets: 2,
    attemptsAllowed: 5,
    freeAttempts: 0,
    price: 599,
    gst: 107.82,
    total: 706.82,
    description: "Comprehensive scenario-based questioning with complex follow-up questions.",
    topics: ["Complex Scenarios", "Inconsistency Probing", "Documentation Deep Dive", "Officer Challenges"],
  },
};

// ─── INTERVIEW QUESTIONS BY MODULE ────────────────────────────────────────────
export const questionsByModule = {
  easy: {
    set1: [
      { id: 1, category: "Purpose of Travel", question: "Good morning. What is the purpose of your visit to the United States?", hint: "Be clear and concise about your travel purpose — tourism, business, or visiting family." },
      { id: 2, category: "Duration of Stay", question: "How long do you intend to stay in the United States?", hint: "Mention the specific duration and ensure it aligns with your visa type." },
      { id: 3, category: "Financial Support", question: "Who will be funding your trip to the United States?", hint: "Clearly explain your funding source — personal savings, employer, or sponsor." },
      { id: 4, category: "Employment", question: "What is your current occupation?", hint: "Provide your job title, employer name, and duration of employment." },
      { id: 5, category: "Accommodation", question: "Where will you be staying during your visit?", hint: "Name the hotel, address, or the person you will be staying with." },
      { id: 6, category: "Travel Plans", question: "Which cities do you plan to visit in the United States?", hint: "List specific cities and briefly state what you plan to do in each." },
      { id: 7, category: "Return Plans", question: "When do you plan to return to your home country?", hint: "State your return date and confirm you have a return ticket booked." },
      { id: 8, category: "Prior Visas", question: "Have you previously been issued a US visa?", hint: "Mention previous visa approvals, their validity, and whether you used them." },
      { id: 9, category: "Documentation", question: "Do you have a confirmed travel itinerary for your trip?", hint: "Mention flight bookings, hotel reservations, and any tour packages." },
      { id: 10, category: "DS-160", question: "Is all information provided in your DS-160 form accurate and current?", hint: "Confirm accuracy or mention any changes since the form was submitted." },
    ],
    set2: [
      { id: 1, category: "Destination Choice", question: "Why did you choose the U.S. over other countries?", hint: "Highlight specific reasons like education quality, tourism appeal, or business opportunities unique to the US." },
      { id: 2, category: "Travel Plan", question: "Can you explain your full travel/study/work plan?", hint: "Provide a clear, step-by-step overview of your itinerary, study program, or work schedule." },
      { id: 3, category: "Financial Sponsor", question: "Who is paying for your trip?", hint: "State clearly who is funding — self, sponsor, or employer — and mention you have documentation ready." },
      { id: 4, category: "Return Intent", question: "What will you do after returning to your country?", hint: "Describe concrete plans — a job offer, continuing studies, or family responsibilities awaiting you." },
      { id: 5, category: "Final Appeal", question: "Why should we grant you this visa?", hint: "Summarise your strongest points: genuine intent, financial stability, and strong home country ties." },
      { id: 6, category: "Home Country Ties", question: "What ties do you have to your home country?", hint: "List specific ties — employment contract, family dependents, property ownership, or ongoing education." },
      { id: 7, category: "Academic Choice", question: "If you are a student: Why this university/course?", hint: "Explain the university's ranking, specific program strengths, and how it aligns with your career goals." },
      { id: 8, category: "Tourism Itinerary", question: "If tourist: What places will you visit?", hint: "Name 3-4 specific cities, landmarks, or events you plan to visit with approximate dates." },
      { id: 9, category: "Employment Details", question: "If work visa: What is your role and employer?", hint: "State your job title, employer name, key responsibilities, and how this role benefits your career." },
      { id: 10, category: "Rejection Plan", question: "What will you do if your visa is rejected?", hint: "Show resilience — you will respect the decision, re-evaluate your application, and reapply if eligible." },
    ],
  },
  medium: {
    set1: [
      { id: 1, category: "Travel Plan", question: "Can you walk me through your complete plan in the U.S.?", hint: "Provide a clear chronological overview of your itinerary or program schedule." },
      { id: 2, category: "Destination Choice", question: "Why did you choose this specific location over others?", hint: "Explain specific advantages of the chosen city or institution compared to alternatives." },
      { id: 3, category: "Preparation", question: "How did you prepare for this trip/study/work opportunity?", hint: "Mention language tests, admissions steps, visa research, or logistical arrangements you made." },
      { id: 4, category: "Daily Routine", question: "What exactly will you do on a typical day in the U.S.?", hint: "Describe your daily schedule — classes, work tasks, or planned tourist activities." },
      { id: 5, category: "Future Goals", question: "How is this trip beneficial for your future?", hint: "Connect the US experience to your career progression or personal development back home." },
      { id: 6, category: "Accommodation", question: "Who arranged your accommodation and why there?", hint: "Name the hotel, university housing, or host, and explain the choice based on location or budget." },
      { id: 7, category: "Research & Awareness", question: "What research did you do before applying for this visa?", hint: "Show you understand visa conditions, local laws, and your program's requirements." },
      { id: 8, category: "Financial Planning", question: "Can you explain your financial planning for this trip?", hint: "Detail your budget allocation — travel, living costs, emergencies — and how you will cover it." },
      { id: 9, category: "Adaptability", question: "What challenges do you expect during your stay?", hint: "Acknowledge potential difficulties like culture shock or costs, and show how you will handle them." },
      { id: 10, category: "Consistency Check", question: "How does this plan align with what you stated in your DS-160?", hint: "Confirm that your verbal answers match the written application and note any minor clarifications." },
    ],
    set2: [
      { id: 1, category: "Current Status", question: "Tell me about your current job/studies in detail.", hint: "Provide your exact role, responsibilities, institution, or course structure and duration." },
      { id: 2, category: "Timing", question: "Why is this the right time for you to travel/go abroad?", hint: "Explain why now fits your academic break, career stage, or financial readiness." },
      { id: 3, category: "Experience Alignment", question: "How does this trip align with your past experience?", hint: "Connect your previous work or studies to the purpose of this US visit." },
      { id: 4, category: "Prior Refusals", question: "Have you ever been refused a visa before? Why?", hint: "Answer honestly. If yes, state the reason briefly and what has improved since then." },
      { id: 5, category: "History Gaps", question: "Can you explain any gaps in your education or work history?", hint: "Provide a clear, factual reason — health, family care, skill-building, or job searching." },
      { id: 6, category: "Future Goals", question: "What are your long-term career plans?", hint: "Show a concrete 5-year plan that requires your return to your home country to execute." },
      { id: 7, category: "Support Network", question: "Who are the key people supporting your application?", hint: "Name your sponsor, employer, or academic mentor and their relationship to you." },
      { id: 8, category: "Contingency Planning", question: "What will you do if your plans in the U.S. change?", hint: "Show flexibility but reaffirm your primary goal and intention to return on schedule." },
      { id: 9, category: "Financial Buffer", question: "How will you manage expenses if costs increase?", hint: "Mention your savings cushion, backup sponsor, or emergency fund available for unexpected costs." },
      { id: 10, category: "Domestic Alternatives", question: "Why not pursue this opportunity in your home country?", hint: "Highlight the unique value the US offers — specific programs, industry exposure, or cultural experience — unavailable locally." },
    ],
    set3: [
      { id: 1, category: "Return Guarantee", question: "What guarantees do you have that you will return?", hint: "Point to legally binding commitments — employment contracts, enrolled courses, or family responsibilities." },
      { id: 2, category: "Immigration Intent", question: "Why shouldn't we think you might stay illegally?", hint: "Emphasise your strong socioeconomic ties and the severe consequences of overstaying for your future." },
      { id: 3, category: "Home Country Ties", question: "What strong ties do you have to your home country?", hint: "Mention specific assets — property deeds, business ownership, immediate family — that anchor you here." },
      { id: 4, category: "Employment Opportunity", question: "If you get a job opportunity in the U.S., what will you do?", hint: "State clearly that you will not accept unauthorized employment and will return as scheduled." },
      { id: 5, category: "US Relatives", question: "Do you have relatives in the U.S.? What do they do?", hint: "Be transparent about relatives, their immigration status, and your limited contact with them." },
      { id: 6, category: "Sponsor Motivation", question: "Why is your sponsor willing to support you?", hint: "Explain the relationship and the sponsor's financial capacity, supported by documents if available." },
      { id: 7, category: "Return Plans", question: "What will you do immediately after returning?", hint: "Describe your first week back — returning to work, resuming classes, or submitting required reports." },
      { id: 8, category: "Trip Importance", question: "How important is this trip for you personally and professionally?", hint: "Connect the visit to a critical milestone — a required conference, a key course module, or a planned business deal." },
      { id: 9, category: "Denial Contingency", question: "If your visa is denied, what is your backup plan?", hint: "Show you have considered alternatives — deferring studies, applying later, or pursuing options locally — but this is your primary goal." },
      { id: 10, category: "Summary", question: "Can you summarize your entire purpose in one sentence?", hint: "Be concise — e.g., 'To complete my master's in Data Science at XYZ University and return to my current employer's analytics team.'"},
    ],
  },
  difficult: {
    set1: [
      { id: 1, category: "Purpose of Visit", question: "What is the purpose of your visit to the United States?", hint: "State your purpose clearly and concisely. Be specific about the activity, event, or program." },
      { id: 2, category: "Duration of Stay", question: "How long do you plan to stay?", hint: "Give exact dates and ensure they match your flight bookings and DS-160 declaration." },
      { id: 3, category: "Return Guarantee", question: "What guarantees do you have that you will return?", hint: "Mention concrete legal or financial obligations that ensure your return." },
      { id: 4, category: "Immigration Intent", question: "Why shouldn't we think you might stay illegally?", hint: "Address the concern calmly. Highlight your track record and the severe risks of overstaying." },
      { id: 5, category: "Home Country Ties", question: "What strong ties do you have to your home country?", hint: "List your strongest anchors: family dependents, property, business, or career trajectory." },
      { id: 6, category: "Employment Compliance", question: "If you get a job opportunity in the U.S., what will you do?", hint: "Affirm that you will not accept unauthorized work and will comply strictly with visa terms." },
      { id: 7, category: "US Relatives", question: "Do you have relatives in the U.S.? What do they do?", hint: "Disclose all relatives honestly. Explain their status and why their presence does not influence you to stay." },
      { id: 8, category: "Sponsor Motivation", question: "Why is your sponsor willing to support you?", hint: "Clarify your relationship to the sponsor and their financial capacity and willingness." },
      { id: 9, category: "Immediate Return Plans", question: "What will you do immediately after returning?", hint: "Describe a specific action you will take within days of returning — resuming work or studies." },
      { id: 10, category: "Trip Importance", question: "How important is this trip for you personally and professionally?", hint: "Connect the visit to a critical milestone that requires this specific timing and location." },
      { id: 11, category: "Contingency Plan", question: "If your visa is denied, what is your backup plan?", hint: "Show you have a realistic Plan B — reapplying later or pursuing a local alternative." },
      { id: 12, category: "Summary", question: "Can you summarize your entire purpose in one sentence?", hint: "Deliver a powerful, concise statement covering purpose, duration, and firm intent to return." },
    ],
    set2: [
      { id: 1, category: "Opportunity Pressure", question: "If you get a better opportunity in the U.S., would you stay longer?", hint: "State clearly that your visa conditions and personal commitments prevent any unauthorized extension." },
      { id: 2, category: "Settlement Trend", question: "Why do many applicants with your profile try to settle abroad?", hint: "Acknowledge the trend but distinguish yourself by emphasizing your specific reasons to return." },
      { id: 3, category: "Return Barriers", question: "What is stopping you from not returning?", hint: "List the strongest deterrents: legal consequences, career loss, or family separation." },
      { id: 4, category: "Family Relocation", question: "If your family moved to the U.S., would you still return?", hint: "Explain that your return decision is based on your own ties, not just family location." },
      { id: 5, category: "Trust & Intent", question: "Why do you think we should trust your intent?", hint: "Point to your consistent documentation, clear history, and the logic of your career plan." },
      { id: 6, category: "Loss Assessment", question: "What would you lose if you didn't return to your home country?", hint: "Quantify the loss: career reputation, property, business contracts, or educational standing." },
      { id: 7, category: "Extension Scenarios", question: "Is there any scenario where you would extend your stay?", hint: "Only mention legal, pre-approved extensions for genuine reasons, and reaffirm your plan to return." },
      { id: 8, category: "Hypothetical Laws", question: "If laws were relaxed, would you prefer staying permanently?", hint: "Answer honestly but clarify that your current life and goals are rooted in your home country." },
      { id: 9, category: "Ties Breakdown", question: "What ties are emotional vs practical in your case?", hint: "Distinguish clearly: Emotional (family/friends) vs Practical (property/job contracts/financial assets)." },
      { id: 10, category: "Final Conviction", question: "Convince me in one sentence that you are not an immigration risk.", hint: "Deliver a strong, factual summary of your ties and compliance history." },
    ],
    set3: [
      { id: 1, category: "Self Introduction", question: "Can you briefly introduce yourself and your current situation?", hint: "Give a concise professional and personal summary relevant to your visa application." },
      { id: 2, category: "Purpose of Travel", question: "What is the purpose of your visit to the United States?", hint: "Be clear, direct, and specific about the main goal of your trip." },
      { id: 3, category: "Financial Planning", question: "How have you planned to manage your finances for this trip/stay?", hint: "Outline your budget sources: personal savings, sponsor, loans, or employer support." },
      { id: 4, category: "Financial Probe", question: "Your financial plan seems tight—what if costs increase significantly?", hint: "Explain your financial buffer, emergency funds, or contingency plan for overages." },
      { id: 5, category: "Sponsor Verification", question: "Why should we believe your sponsor can truly support you?", hint: "Reference your sponsor's stable income, assets, and past history of support." },
      { id: 6, category: "Contingency Planning", question: "If your sponsor withdraws support suddenly, what will you do?", hint: "Show self-reliance by mentioning your own backup savings or alternative funding sources." },
      { id: 7, category: "Post-Stay Finances", question: "How much money will you realistically have left after your stay?", hint: "Demonstrate that returning home is financially viable and you won't be destitute." },
      { id: 8, category: "Independence Check", question: "Are you financially independent? If not, why at this stage?", hint: "Honest explanation—students may be supported by parents; young professionals may still be building savings." },
      { id: 9, category: "Investment Rationale", question: "Why invest this amount abroad instead of in your home country?", hint: "Highlight the unique value, ROI, or necessity that only the US trip provides." },
      { id: 10, category: "Documentation Alignment", question: "Your answers and documents must align—if I find a mismatch, how would you explain it?", hint: "Acknowledge the importance of accuracy and offer to clarify any documentation promptly." },
    ],
    set4: [
      { id: 1, category: "True Intent", question: "What is the real reason you want to go to the U.S. instead of staying where you are already successful?", hint: "Highlight the unique value, education, or experience that cannot be obtained locally." },
      { id: 2, category: "Self Reflection", question: "If we verify your entire life history, what is the one thing that might raise concern?", hint: "Be proactive about any gaps or anomalies and explain them honestly before being asked." },
      { id: 3, category: "Critical Thinking", question: "What part of your application would you personally question if you were the officer?", hint: "Identify a potential weak point and provide a strong justification for it." },
      { id: 4, category: "Anticipation", question: "If your visa is denied today, what exact reason do you think it would be?", hint: "Show self-awareness of potential red flags and how you can address or mitigate them." },
      { id: 5, category: "Direct Question", question: "Are you using this visa as a stepping stone for immigration—yes or no? Explain.", hint: "Answer clearly 'no' and explain the difference between temporary travel and permanent migration." },
      { id: 6, category: "Sacrifice & Commitment", question: "What have you sacrificed in your home country to pursue this opportunity?", hint: "Show that you are leaving something valuable behind, proving your intention to return and reclaim it." },
      { id: 7, category: "Comparative Choice", question: "If someone offered you the same opportunity locally, would you still go? Why?", hint: "Explain what specifically makes the US experience unique or irreplaceable." },
      { id: 8, category: "Weakness Probe", question: "What is the weakest point in your financial or personal profile?", hint: "Be honest, but immediately follow up with how you compensate or mitigate that weakness." },
      { id: 9, category: "Absolute Assurance", question: "How do we know you will not overstay under any circumstance?", hint: "Point to legal, financial, and family consequences that make overstaying an impossible choice." },
      { id: 10, category: "Final Transparency", question: "What is the one truth about your trip you are afraid we might misunderstand?", hint: "Use this to clarify any nuance or context that isn't obvious in your paperwork." },
    ],
    set5: [
      { id: 1, category: "Employment Compliance", question: "If you get a job offer in the U.S., will you stay?", hint: "Affirm strict adherence to visa terms. Unauthorized work is illegal and violates your trust." },
      { id: 2, category: "Immigration Barriers", question: "What stops you from settling permanently in the U.S.?", hint: "Highlight the severe consequences: deportation, permanent bans, and loss of family/career at home." },
      { id: 3, category: "Home Country Value", question: "Why do you need to return if life is better abroad?", hint: "Explain non-financial reasons for returning: family obligations, cultural roots, and long-term career goals." },
      { id: 4, category: "Core Ties", question: "What ties are strong enough to pull you back home?", hint: "Emphasize legally binding commitments, property ownership, or deep family dependencies." },
      { id: 5, category: "Legal Compliance", question: "Be honest—would you consider staying illegally if life is hard back home?", hint: "State unequivocally no. Overstaying ruins your future and permanently separates you from your home." },
      { id: 6, category: "Extension Scenarios", question: "If your visa is extended, what would you do?", hint: "Confirm strict adherence to original authorized dates unless legally extended for valid, pre-approved reasons." },
      { id: 7, category: "Trust Building", question: "Why should we trust your intention is temporary?", hint: "Point to your consistent documentation, financial stability, and strong home country ties." },
      { id: 8, category: "Life Priorities", question: "What is more important to you: opportunity or nationality?", hint: "Emphasize that your nationality, family roots, and long-term community outweigh temporary gains." },
      { id: 9, category: "Emotional Roots", question: "What keeps you emotionally attached to your home country?", hint: "Mention specific relationships, cultural bonds, or community roles that require your presence." },
      { id: 10, category: "Decision Integrity", question: "If your future depends on staying, what will you choose?", hint: "Reaffirm your commitment to legal compliance and returning to fulfill your obligations at home." },
    ],
    set6: [
      { id: 1, category: "Scenario Challenge", question: "You say this is a two-week trip, but your leave approval from work covers only five days. Explain the mismatch.", hint: "Clarify whether the extra days are weekends, public holidays, or if you need to request extended leave." },
      { id: 2, category: "Inconsistency Probe", question: "Your invitation letter is dated after your visa application. Did you apply before receiving the invitation?", hint: "Confirm the timeline honestly — preliminary plans followed by a formal invitation — and show the sequence of events." },
      { id: 3, category: "Documentation Deep Dive", question: "Your hotel reservation was cancelled yesterday. Have your travel plans changed?", hint: "Explain if it was a rebooking, a platform error, or an actual change — and show the new reservation." },
      { id: 4, category: "Intent Scrutiny", question: "Your partner holds a student visa in the US. Are you travelling to join them permanently?", hint: "State clearly that this is a temporary visit and that your own ties — career, family, assets — require your return." },
      { id: 5, category: "Financial Probe", question: "Your credit card statement shows significant outstanding debt. How can you afford this trip?", hint: "Distinguish between manageable revolving credit and actual financial strain, and show disposable income available for travel." },
      { id: 6, category: "Complex Ties", question: "You work remotely — what physically ties you to your home country if your job is location-independent?", hint: "Point to your company's registered office, tax residency, client base, or personal property and family commitments." },
      { id: 7, category: "Overstay Risk", question: "A previous visa you held was cancelled at the border. Can you explain what happened?", hint: "Provide the factual sequence — why the officer cancelled it, how you complied with departure, and what has changed since." },
      { id: 8, category: "Purpose Contradiction", question: "Your stated purpose is attending a conference, yet you have no registration confirmation. Are you sure you are going?", hint: "If registered, produce the confirmation. If pending, explain the registration process and show correspondence." },
      { id: 9, category: "Officer Challenge", question: "I have interviewed thousands of applicants and your case raises several red flags. Respond to each one.", hint: "Stay composed, address each concern methodically with evidence, and maintain a respectful tone throughout." },
      { id: 10, category: "Final Assessment", question: "Convince me in thirty seconds that you are a genuine visitor who will depart on time.", hint: "Summarise your strongest ties, the specific purpose of travel, and your track record of international compliance." },
    ],
    set7: [
    {
      id: 1,
      category: "Travel Logic",
      question: "You are traveling with a group, yet your application was submitted separately. Can you explain the timeline and reason for this difference?",
      hint: "Focus on process clarity — different application timing, independent eligibility, or administrative reasons."
    },
    {
      id: 2,
      category: "Data Consistency",
      question: "There is a mismatch between your declared occupation and publicly available information. Can you clarify your current professional status?",
      hint: "Give a precise, factual explanation — recent transition, dual roles, or outdated public data."
    },
    {
      id: 3,
      category: "Financial Credibility",
      question: "Your financial documents show sufficient funds, but your income history appears inconsistent. How do you justify your financial stability?",
      hint: "Explain income sources clearly — savings, business revenue, family support, or asset liquidation."
    },
    {
      id: 4,
      category: "Intent Verification",
      question: "What guarantees do you have that ensure your return after the visit, beyond verbal assurance?",
      hint: "Present concrete ties — employment contracts, ongoing projects, property, or dependent family members."
    },
    {
      id: 5,
      category: "Purpose Depth",
      question: "Can you break down your travel itinerary day-by-day and explain how each activity aligns with your stated purpose?",
      hint: "Be specific and structured — vague answers raise doubts."
    },
    {
      id: 6,
      category: "Risk Assessment",
      question: "Given your current financial and professional stage, why is this the right time for international travel?",
      hint: "Link timing to opportunity — career break, event, business need, or personal milestone."
    },
    {
      id: 7,
      category: "Application History",
      question: "You have applied multiple times previously. What measurable changes have occurred in your profile since your last application?",
      hint: "Be evidence-driven — salary increase, new role, improved finances, or stronger ties."
    },
    {
      id: 8,
      category: "Network Influence",
      question: "Do you have any close contacts or extended network in the destination country who could influence your stay duration?",
      hint: "Answer honestly, but clarify independence and intent to comply with visa conditions."
    },
    {
      id: 9,
      category: "Credibility Test",
      question: "If we verify your claims independently, is there anything that might appear inconsistent or unclear?",
      hint: "Show transparency — acknowledge minor gaps proactively instead of being defensive."
    },
    {
      id: 10,
      category: "Decision Trigger",
      question: "What is the single strongest piece of objective evidence that demonstrates your intent to return?",
      hint: "Choose something verifiable and binding — employment agreement, business ownership, or legal responsibility."
    }
  ]
  },
};

// ─── IMPROVEMENT TIPS ─────────────────────────────────────────────────────────
export const improvementTips = [
  { tip: "Keep answers concise", detail: "Most answers should be two to three sentences. Avoid over-explaining or going off-topic." },
  { tip: "Demonstrate strong ties to home country", detail: "Always connect your answer back to why you will return — employment, family, or property." },
  { tip: "Maintain consistency with your DS-160", detail: "Your verbal answers must align precisely with your DS-160 form. Review it before your actual interview." },
  { tip: "Project confidence throughout", detail: "Speak clearly, maintain eye contact, and do not hesitate on straightforward questions." },
];

// ─── VOICE CONFIGURATION ──────────────────────────────────────────────────────
export const voiceConfig = {
  male: {
    name: "Officer James Mitchell",
    pitch: 0.85,
    rate: 0.82,
    gender: "male",
    badge: "Senior Consular Officer",
    avatar: "👨‍💼",
    avatarBg: "linear-gradient(135deg, #1e3a5f, #2d5a8e)",
    accentColor: "#4f8ef7",
    preferredVoiceNames: ["male", "david", "mark", "daniel", "alex"],
  },
  female: {
    name: "Officer Sarah Williams",
    pitch: 1.15,
    rate: 0.82,
    gender: "female",
    badge: "Consular Officer",
    avatar: "👩‍💼",
    avatarBg: "linear-gradient(135deg, #3b1f5e, #6b3fa0)",
    accentColor: "#a78bfa",
    preferredVoiceNames: ["samantha", "karen", "victoria", "female", "zira", "susan"],
  },
};

// ─── HOW IT WORKS STEPS ───────────────────────────────────────────────────────
export const howItWorksSteps = [
  { step: "01", title: "Select Module", desc: "Choose from Foundation, Intermediate, or Advanced difficulty levels based on your preparation stage.", icon: "🎯" },
  { step: "02", title: "Choose Officer", desc: "Select a male or female AI Consular Officer and begin your simulated interview session.", icon: "👤" },
  { step: "03", title: "Speak Your Answers", desc: "Answer questions aloud as you would in an actual embassy interview. The AI listens in real time.", icon: "🎙️" },
  { step: "04", title: "Receive Assessment", desc: "Get a detailed scorecard with AI-evaluated feedback and an ideal answer for each question.", icon: "📊" },
];

// ─── SCORE CATEGORIES ─────────────────────────────────────────────────────────
export const scoreCategories = [
  { key: "clarity",     label: "Answer Clarity",   color: "#4f8ef7" },
  { key: "confidence",  label: "Confidence",        color: "#a78bfa" },
  { key: "consistency", label: "Consistency",       color: "#22c55e" },
  { key: "relevance",   label: "Relevance",         color: "#f59e0b" },
];

// ─── PRICING ─────────────────────────────────────────────────────────────────
export const PRICING = {
  medium: { base: 299, gstRate: 0.18 },
  difficult: { base: 599, gstRate: 0.18 },
};

// ─── AI EVALUATION ────────────────────────────────────────────────────────────
export async function evaluateAnswersWithAI(answers, questions) {
  const preProcessedEvaluations = [];
  const answersForAI = [];

  answers.forEach((a) => {
    const isNoAnswer =
      !a.answer ||
      a.answer.trim() === "" ||
      NO_ANSWER_MARKERS.some((marker) => a.answer.toLowerCase().includes(marker));

    if (isNoAnswer) {
      preProcessedEvaluations.push({
        questionId: a.questionId,
        idealAnswer: "A clear, concise, and honest answer demonstrating strong ties to home country.",
        score: 0,
        remark: "No answer was provided. This results in zero marks for this question.",
      });
    } else {
      answersForAI.push(a);
    }
  });

  if (answersForAI.length === 0) {
    const allScores = preProcessedEvaluations.map((e) => e.score);
    const avg = allScores.length > 0 ? allScores.reduce((a, b) => a + b, 0) / allScores.length : 0;
    return {
      evaluations: preProcessedEvaluations,
      scores: {
        clarity: Math.round(avg),
        confidence: Math.round(avg),
        consistency: Math.round(avg),
        relevance: Math.round(avg),
      },
    };
  }

  const qaPairs = answersForAI.map((a) => {
    const q = questions.find((qItem) => qItem.id === a.questionId);
    return {
      questionId: a.questionId,
      category: q?.category || "",
      question: q?.question || "",
      answer: a.answer,
    };
  });

  const aiResult = await interviewService.evaluateAnswers({
    interviewType: "us_visa_mock",
    answers: qaPairs,
  });

  const allEvaluations = [...preProcessedEvaluations, ...aiResult.evaluations];
  allEvaluations.sort((a, b) => a.questionId - b.questionId);

  const allScores = allEvaluations.map((e) => e.score);
  const avgScore = allScores.length > 0 ? allScores.reduce((a, b) => a + b, 0) / allScores.length : 0;

  return {
    evaluations: allEvaluations,
    scores: {
      clarity: Math.round(avgScore),
      confidence: Math.round(avgScore),
      consistency: Math.round(avgScore),
      relevance: Math.round(avgScore),
    },
  };
}

// ─── COMPANY INFO ─────────────────────────────────────────────────────────────
export const companyInfo = {
  name: "The Visa Co",
  tagline: "AI-Powered Visa Preparation Tool",
  email: "support@thevisaco.com",
  website: "www.thevisaco.com",
  address: "123 Visa Street, Suite 100, New Delhi, India",
};

// ─── PAYMENT OVERLAY CONTENT ──────────────────────────────────────────────────
export const PAYMENT_OVERLAY_CONTENT = {
  title: "Complete Payment",
  subtitle: "Secure your access to continue practicing",
  buttonLabel: "Pay Now",
  secureNote: "Secure payment • All transactions encrypted • The Visa Co",
  termsText: "By proceeding, you agree to the Terms of Service. All purchases are final due to the digital nature of this service.",
  successMessage: "Payment Successful — Access Granted",
};

export const TERMS_AND_CONDITIONS_CONTENT = {
  title: "Terms & Conditions",
  sections: [
    {
      heading: "1. Introduction",
      body: 'This Consent Form ("Agreement") is entered into between the user ("Participant") and the service provider ("Provider") offering AI-powered mock interview preparation for U.S. visa applications. By accessing or using this service, you agree to the terms outlined below.',
    },
    {
      heading: "2. Nature of the Service",
      body: "The Provider offers an AI-based mock interview simulation designed to help participants prepare for U.S. visa interviews. The service includes: Simulated interview questions, Automated responses and feedback, Performance analysis and suggestions. This service is intended for practice and educational purposes only.",
    },
    {
      heading: "3. No Guarantee of Visa Approval",
      body: "The Participant acknowledges that: This service does NOT guarantee visa approval. The final decision regarding any visa application is made solely by the respective U.S. embassy or consulate. The Provider is not affiliated with any government authority, including U.S. immigration authorities.",
    },
    {
      heading: "4. Use of Artificial Intelligence",
      body: "The Participant understands that: The mock interview is conducted using artificial intelligence. Responses, feedback, and scoring are generated automatically. AI outputs may not always be fully accurate, complete, or reflective of real interview outcomes.",
    },
    {
      heading: "5. Data Collection and Usage",
      body: "By using this service, the Participant consents to: Collection of personal inputs (e.g., answers, interview responses), Use of this data to improve the service experience, Storage of responses for performance tracking and analysis. The Provider agrees to: Not sell or misuse personal data, Take reasonable steps to protect user data.",
    },
    {
      heading: "6. Confidentiality",
      body: "The Provider will maintain reasonable confidentiality of participant data. However, the Participant acknowledges that no digital system is completely secure.",
    },
    {
      heading: "7. User Responsibility",
      body: "The Participant agrees to: Provide truthful and accurate information, Use the service for lawful purposes only, Not misuse, copy, or distribute the service content without permission.",
    },
    {
      heading: "8. Limitation of Liability",
      body: "The Provider shall not be held liable for: Any visa rejection or delay, Decisions made by immigration authorities, Any loss arising from reliance on AI-generated feedback.",
    },
    {
      heading: "9. Acceptance of Terms",
      body: "By proceeding with the AI mock interview, the Participant: Confirms that they have read and understood this Agreement, Voluntarily consents to participate in the service.",
    },
  ],
};

// ─── FLATTENED INTERVIEW QUESTIONS (easy set1 as default) ─────────────────────
export let interviewQuestions = questionsByModule.easy.set1;

// ─── HELPER: Get questions by module ID and set name ──────────────────────────
export function getQuestionsByModule(moduleId, setName) {
  const moduleMap = {
    easy: questionsByModule.easy,
    medium: questionsByModule.medium,
    pro: questionsByModule.difficult,
    difficult: questionsByModule.difficult,
  };
  const mod = moduleMap[moduleId];
  if (!mod) return questionsByModule.easy.set1;
  if (setName && mod[setName]) return mod[setName];
  return mod.set1;
}

// ─── HELPER: Get all available sets for a module ──────────────────────────────
export function getModuleSets(moduleId) {
  const mod = {
    easy: questionsByModule.easy,
    medium: questionsByModule.medium,
    pro: questionsByModule.difficult,
    difficult: questionsByModule.difficult,
  }[moduleId];
  if (!mod) return { set1: questionsByModule.easy.set1 };
  return mod;
}

// ─── REWARD PAYMENT CONSTANTS ─────────────────────────────────────────────────
export const REWARD_LABELS = {
  walletSectionTitle: "Amount to use from wallet",
  inputPlaceholder: "Enter amount",
  currencySymbol: "₹",
  toggleLabel: "Use Rewards Balance",
  toggleAvailable: "Available",
  toggleUnavailable: "Insufficient balance",
  toggleMinFeeRequired: `Min ₹ fee required`,
  lockedBadge: "Insufficient Balance",
  lockedHint: "Add more rewards to unlock full reward payment",
};

export const REWARD_QUICK_BUTTONS = [
  { key: "full", label: (price) => `Full price (₹${price})`, compute: (price, total) => Math.min(price, total) },
  { key: "max", label: (total) => `Max balance (₹${total})`, compute: (price, total) => total },
  { key: "half", label: (price) => `Half (₹${Math.floor(price / 2)})`, compute: (price, total) => Math.floor(price / 2), variant: "half" },
];

export const REWARD_LIMIT = {
  maxDiscountPercent: 20,
  maxDiscountLabel: "20%",
  calculatedOn: "base price",
};

export const REWARD_MESSAGES = {
  enterAmount: "Enter amount to pay with Rewards",
  amountExceedsBalance: (total) => `Amount exceeds your total balance of ₹${total.toLocaleString("en-IN")}`,
  amountExceedsPrice: (price) => `Amount exceeds the price of ₹${price.toLocaleString("en-IN")}`,
  amountExceedsMaxDiscount: (maxDiscount) => `Max reward discount is ₹${maxDiscount.toLocaleString("en-IN")} (${REWARD_LIMIT.maxDiscountLabel} of base price)`,
  insufficientBalance: (price, total) => `Insufficient balance. You need ₹${(price - total).toLocaleString("en-IN")} more.`,
  enterAmountError: "Please enter an amount to use from your balance.",
  rewardBreakdown: (reward, remaining) => `₹${reward.toLocaleString("en-IN")} from wallet · ₹${remaining.toLocaleString("en-IN")} remaining to pay`,
  remainingBalance: (balance) => `Your remaining balance after this: ₹${balance.toLocaleString("en-IN")}`,
  payWithRewards: (reward) => `Pay with Rewards — ₹${reward.toLocaleString("en-IN")}`,
  paySplit: (reward, remaining) => `Pay ₹${reward.toLocaleString("en-IN")} + ₹${remaining.toLocaleString("en-IN")} remaining`,
  rewardAppliedLine: "Reward Discount",
};

export const PRICE_BREAKDOWN_LABELS = {
  basePrice: "Base Price",
  gst: (percent) => `GST (${percent}%)`,
  convenienceFee: "Convenience Fee",
  free: "Free",
  rewardDiscount: "Reward Discount",
  total: "Total",
  subtotal: "Subtotal",
};

// ─── PHONE CODE OPTIONS ────────────────────────────────────────────────────────
export const PHONE_CODE_OPTIONS = [
  { value: "+91", label: "IN +91" },
  { value: "+1", label: "US/CA +1" },
  { value: "+44", label: "UK +44" },
  { value: "+61", label: "AU +61" },
  { value: "+65", label: "SG +65" },
  { value: "+971", label: "AE +971" },
  { value: "+49", label: "DE +49" },
  { value: "+33", label: "FR +33" },
  { value: "+39", label: "IT +39" },
  { value: "+81", label: "JP +81" },
  { value: "+86", label: "CN +86" },
  { value: "+82", label: "KR +82" },
  { value: "+7", label: "RU +7" },
  { value: "+55", label: "BR +55" },
  { value: "+52", label: "MX +52" },
  { value: "+31", label: "NL +31" },
  { value: "+46", label: "SE +46" },
  { value: "+47", label: "NO +47" },
  { value: "+45", label: "DK +45" },
  { value: "+358", label: "FI +358" },
  { value: "+41", label: "CH +41" },
  { value: "+43", label: "AT +43" },
  { value: "+32", label: "BE +32" },
  { value: "+353", label: "IE +353" },
  { value: "+64", label: "NZ +64" },
  { value: "+27", label: "ZA +27" },
  { value: "+234", label: "NG +234" },
  { value: "+254", label: "KE +254" },
  { value: "+233", label: "GH +233" },
  { value: "+20", label: "EG +20" },
  { value: "+966", label: "SA +966" },
  { value: "+92", label: "PK +92" },
  { value: "+880", label: "BD +880" },
  { value: "+62", label: "ID +62" },
  { value: "+63", label: "PH +63" },
  { value: "+66", label: "TH +66" },
  { value: "+84", label: "VN +84" },
  { value: "+60", label: "MY +60" },
];

// ─── NO-ANSWER MARKERS ─────────────────────────────────────────────────────────
export const NO_ANSWER_RECORDED = "No answer recorded.";
export const NO_ANSWER_PROVIDED = "(No answer provided)";
export const NO_ANSWER_WAS_PROVIDED = "No answer was provided for this question.";

export const NO_ANSWER_MARKERS = [
  NO_ANSWER_RECORDED.toLowerCase(),
  NO_ANSWER_PROVIDED.toLowerCase(),
  NO_ANSWER_WAS_PROVIDED.toLowerCase(),
];

// ─── TIMER DURATION (seconds) ──────────────────────────────────────────────────
export const TIMER_DURATION = 60;

// ─── INTERVIEW GREETINGS ───────────────────────────────────────────────────────
export const INTERVIEW_GREETINGS = {
  morning: "morning",
  afternoon: "afternoon",
};

// ─── INTRO SPEECH TEMPLATE ─────────────────────────────────────────────────────
export function buildIntroSpeech(greeting, officerName, officerBadge) {
  return `Good ${greeting}. I am ${officerName}, a ${officerBadge} at the United States Embassy. Please have a seat. I'll be conducting your visa interview today. Please answer all questions clearly and honestly. Let's begin.`;
}


